import PropTypes from 'prop-types';
import MKBox from 'components/MaterialKit/MKBox';
import MKTypography from 'components/MaterialKit/MKTypography';
import { useAuth } from 'contexts/auth';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getLocaleMap } from 'utils/locales';
import { useNavigate } from 'react-router-dom';
import { formatGroupName, handleErrorResponse } from 'utils/general';
import { getWhatsappMessages } from 'api/whatsapps';
import { Dialog, DialogActions, DialogContent, InputBase, Paper, styled, Switch } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import moment from 'moment';
import DataTable from 'components/DataTable';
import { withLoading } from 'utils/hoc';
import get from 'lodash/get';
import map from 'lodash/map';
import sortBy from 'lodash/sortBy';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import CloseIcon from '@mui/icons-material/Close';
import Select from 'components/Select';
import Button from 'components/Button';
import { getUserIWatchAccounts } from 'api/iwatch';
import { parsePhoneNumberFromString, ParseError } from 'libphonenumber-js/max';

const WhatsappLogSection = ({ header_color, text_color, show_header, extra_params, refresh_second, page_options, brand_options, model_options, source_options, currency_options, setLoading, ...props }) => {
  const { auth, setAuth } = useAuth();
  const navigate = useNavigate();
  const [pageSize, setPageSize] = useState(page_options?.length ? page_options[0] : 10);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [intermediateKeyword, setIntermediateKeyword] = useState('');
  const [keyword, setKeyword] = useState();
  const [whatsappData, setWhatsappData] = useState([]);

  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshTimeInterval, setRefreshTimeInterval] = useState(null);

  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [sourceFilter, setSourceFilter] = useState();
  const [confirmedSourceFilter, setConfirmedSourceFilter] = useState();
  const [sourceOptions, setSourceOptions] = useState([]);

  const [lm, setLm] = useState({});
  useEffect(() => {
    getLocaleMap([])
      .then((response) => {
        setLm(response);
      });
  }, []);

  const CustomizedSwitch = styled((switchProps) => (
    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...switchProps} />
  ))(() => ({
    'width': 40,
    'height': 20,
    'padding': 0,
    '& .MuiSwitch-switchBase': {
      'padding': 0,
      'transitionDuration': '300ms',
      '&.Mui-checked': {
        'transform': 'translateX(20px)',
        'color': '#fff',
        'borderColor': 'white !important',
        '& + .MuiSwitch-track': {
          backgroundColor: '#61e048 !important',
          opacity: 1,
          border: 0,
        },
        '&.Mui-disabled + .MuiSwitch-track': {
          opacity: 0.5,
        },
        '& .MuiSwitch-thumb': {
          borderColor: '#61e048 !important',
        },
      },
      '&.Mui-focusVisible .MuiSwitch-thumb': {
        color: '#33cf4d',
        border: '6px solid #fff',
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 1,
      },
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: 20,
      height: 20,
    },
    '& .MuiSwitch-track': {
      borderRadius: 20,
      height: 20,
      width: 40,
      backgroundColor: 'lightgray',
      opacity: 1,
    },
  }));

  const handleClickContact = useCallback((e, data) => {
    e.stopPropagation();
    const phone = data.phone;
    if (phone) {
      navigate(`/contact_detail?contact_name=${encodeURIComponent(data.author)}&contact_number=${encodeURIComponent(phone)}`);
    }
  }, [navigate]);

  const columns = [
    {
      field: 'id',
      sortable: false,
      renderContent: (data) => {
        const { createddate, group_name, message, phone, author } = data;
        // eslint-disable-next-line no-return-assign, no-param-reassign
        const contactName = author?.split(/\s/).reduce((response, word) => response += word.slice(0, 1).toUpperCase(), '').slice(0, 2);
        return (
          <MKBox mb={1} padding="1rem" maxWidth="100%">
            <MKBox mb={0.5} display="flex" width="100%" alignItems="flex-end" justifyContent="space-between">
              <MKTypography fontSize="14px" color={text_color}>
                {moment(createddate).fromNow()}
              </MKTypography>
              <MKBox display="flex" alignItems="center">
                <MKBox onClick={(e) => handleClickContact(e, data)} bgColor="#3259a1" color="white" display="flex" justifyContent="center" alignItems="center" padding={1} sx={{ borderRadius: '50%', cursor: 'pointer' }} width="40px" height="40px" mr="1rem">
                  {contactName}
                </MKBox>
                <WhatsAppIcon
                  fontSize="large"
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const whatsappPhone = phone.replace(/\D/g, '');
                    if (whatsappPhone) {
                      window.open(`//api.whatsapp.com/send?phone=${encodeURIComponent(whatsappPhone)}`, '_blank');
                    } else {
                      alert('Invalid phone number');
                    }
                  }}
                />
              </MKBox>
            </MKBox>
            <MKBox width="100%" padding={1} style={{ border: '1px solid lightgray' }}>
              <MKTypography mb={1} fontSize="18px" color={text_color}>
                {formatGroupName(group_name)}
              </MKTypography>
              <MKTypography fontSize="16px" color={text_color}>
                {message}
              </MKTypography>
            </MKBox>
          </MKBox>
        );
      },
    },
  ];

  const fetchLogFromApi = useCallback(() => {
    const dataParams = {
      $orderBy: 'createddate desc',
      $skip: currentPage * pageSize,
      $top: pageSize,
      ...(keyword ? {
        'group_name[like]': `%${keyword}%`,
        'message[like]': `%${keyword}%`,
      } : {}),
      ...(confirmedSourceFilter ? {
        'account[in]': confirmedSourceFilter.join(),
      } : {}),
      ...extra_params,
    };
    setLoading(true);
    return getWhatsappMessages(dataParams, { 'X-Store-Operator': 'OR' })
      .then(({ data, headers }) => {
        const contentRange = get(headers, 'content-range');
        const size = Number(contentRange.split('/')[1]);
        setWhatsappData(data);
        setTotalRows(size);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [confirmedSourceFilter, currentPage, extra_params, pageSize, setAuth, setLoading, keyword]);

  const fetchUserIWatchAccountsFromApi = useCallback(() => {
    if (!auth?.user) {
      return Promise.resolve();
    }
    return getUserIWatchAccounts({ $expand: 'iwatch_account', user: auth?.user?.username })
      .then(({ data }) => {
        const iWatchAccounts = sortBy(map(data, 'iwatch_account'), ['sequence']);
        const newSourceAccountOptions = map(iWatchAccounts, (iWatchAccount) => {
          const accountName = iWatchAccount.name;
          const phone = iWatchAccount.phone;
          try {
            const phoneNumber = parsePhoneNumberFromString('+' + phone);
            const formattedPhoneNumber = phoneNumber && phoneNumber.formatInternational() || phone;
            return {
              label: `${iWatchAccount.key} (${accountName}) (${formattedPhoneNumber})`,
              value: phone,
            };
          } catch (e) {
            if (e instanceof ParseError) {
              // Not a phone number, non-existent country, etc.
              console.log(e.message);
            } else {
              throw e;
            }
          }
        });

        // newSourceAccountOptions.unshift({ label: '(none)', value: null });
        setSourceOptions(newSourceAccountOptions);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      });
  }, [auth, setAuth]);

  useEffect(() => {
    fetchLogFromApi();
  }, [fetchLogFromApi]);

  useEffect(() => {
    fetchUserIWatchAccountsFromApi();
  }, [fetchUserIWatchAccountsFromApi]);

  useEffect(() => {
    if (autoRefresh && refreshTimeInterval == null) {
      const refreshDataInterval = setInterval(() => {
        fetchLogFromApi();
      }, refresh_second * 1000);
      setRefreshTimeInterval(refreshDataInterval);
    }
  }, [autoRefresh, refreshTimeInterval, refresh_second, fetchLogFromApi]);

  useEffect(() => {
    if (!autoRefresh && refreshTimeInterval != null) {
      clearInterval(refreshTimeInterval);
      setRefreshTimeInterval(null);
    }
  }, [autoRefresh, refreshTimeInterval]);

  useEffect(() => {
    return () => {
      clearInterval(refreshTimeInterval);
    };
  }, [refreshTimeInterval]);

  // useEffect(() => {
  //   if (source_options) {
  //     setSourceOptions(source_options);
  //   }
  // }, [source_options]);

  const handleResetFilter = useCallback(() => {
    setIsFilterDialogOpen(false);
    setSourceFilter(null);
    setConfirmedSourceFilter(null);
    setCurrentPage(0);
  }, []);

  const handleConfirmFilter = useCallback(() => {
    setIsFilterDialogOpen(false);
    setConfirmedSourceFilter(sourceFilter)
  }, [sourceFilter]);

  return (
    <MKBox
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      width="100%"
      {...props}
    >
      <MKBox
        display="flex"
        width="100%"
        flexDirection="column"
        alignItems="center"
        bgColor={header_color}
        padding="1rem"
      >
        <MKBox
          display="flex"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
        >
          <ArrowBackIcon fontSize="medium" style={{ cursor: 'pointer' }} onClick={() => navigate(-1)} />
          <MKTypography color={text_color} style={{ position: 'absolute', left: '45%' }}>Log</MKTypography>
          <MKBox display="flex" alignItems="center">
            <MKTypography color={text_color} fontSize="14px">Auto Refresh</MKTypography>
            <CustomizedSwitch
              sx={{ m: 1 }}
              checked={autoRefresh}
              onChange={() => setAutoRefresh(!autoRefresh)}
            />
          </MKBox>
        </MKBox>
        <MKBox
          display="flex"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          marginY={1}
        >
          <Paper
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              setKeyword(intermediateKeyword);
              setCurrentPage(0);
            }}
            sx={{ p: 0.5, display: 'flex', alignItems: 'center', width: '100%' }}
          >
            <SearchIcon fontSize="medium" style={{ marginLeft: '1rem' }} />
            <InputBase
              sx={{ ml: 2, flex: 1 }}
              placeholder="Search by keywords"
              onChange={(e) => setIntermediateKeyword(e.target.value)}
            />
            <KeyboardReturnIcon fontSize="medium" style={{ cursor: 'pointer', marginRight: '1rem' }} onClick={() => {
              setKeyword(intermediateKeyword);
              setCurrentPage(0);
            }} />
          </Paper>
          <FilterAltOutlinedIcon fontSize="medium" onClick={() => setIsFilterDialogOpen(true)} style={{ color: text_color, marginLeft: '1rem', cursor: 'pointer' }} />
        </MKBox>
      </MKBox>
      <MKBox display="flex" width="100%">
        <DataTable
          data={whatsappData}
          columns={columns}
          showHeader={show_header}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          rowsPerPage={pageSize}
          rowsPerPageOptions={page_options}
          totalCount={totalRows}
          onRowsPerPageChange={setPageSize}
        />
      </MKBox>
      <Dialog
        open={isFilterDialogOpen}
        fullWidth
      >
        <MKBox display="flex" width="100%" justifyContent="flex-end" padding={1}>
          <CloseIcon fontSize="medium" onClick={() => setIsFilterDialogOpen(false)} style={{ cursor: 'pointer' }} />
        </MKBox>
        <DialogContent>
          <MKTypography fontSize="16px" style={{ marginBottom: '0.25rem', marginTop: '1rem' }}>Source Feeder Account(s)</MKTypography>
          <Select
            value={sourceFilter}
            onChange={(v) => setSourceFilter(v)}
            options={sourceOptions}
            fullWidth
            multiple
            style={{ marginBottom: '1rem' }}
          />
        </DialogContent>
        <DialogActions>
          <MKBox display="flex" width="100%" justifyContent="space-around">
            <Button fullWidth style={{ marginRight: '1rem', backgroundColor: 'white', borderColor: '1px solid black', color: 'black' }} onClick={handleResetFilter}>
              Reset
            </Button>
            <Button fullWidth style={{ backgroundColor: '#eacb37', color: 'black' }} onClick={handleConfirmFilter}>
              Confirm
            </Button>
          </MKBox>
        </DialogActions>
      </Dialog>
    </MKBox>
  );
};

WhatsappLogSection.propTypes = {
  header_color: PropTypes.string,
  text_color: PropTypes.string,
  page_options: PropTypes.string,
  setLoading: PropTypes.func,
  show_header: PropTypes.bool,
  extra_params: PropTypes.object,
  refresh_second: PropTypes.number,
  brand_options: PropTypes.array,
  model_options: PropTypes.array,
  source_options: PropTypes.array,
  currency_options: PropTypes.array,
};

WhatsappLogSection.defaultProps = {
  header_color: '#2c589d',
  text_color: '#000',
  page_options: [],
  setLoading: null,
  show_header: false,
  extra_params: {},
  refresh_second: 10,
  brand_options: [],
  model_options: [],
  source_options: [],
  currency_options: [],
};

export default withLoading(WhatsappLogSection);
